"use client";
// Imports
import Link from "next/link";
import Image from "next/image";
import { FaInstagram, FaSquareFacebook, FaYoutube } from "react-icons/fa6";
import { Container, FooterNav } from "@/components";
import { usePathname } from "next/navigation";

function formatAddressLine({ address, phone }) {
  const addressLine = address?.replace(/\s*\n\s*/g, " ").trim();
  if (addressLine && phone) return `${addressLine} · ${phone}`;
  return addressLine || phone || "";
}

// Footer
export function Footer({ data }) {
  // Variables
  const pathname = usePathname();
  const addressLine = formatAddressLine(data || {});

  // Render
  return (
    <footer
      className={`bg-brand-red-gum text-brand-wool ${
        pathname === "/map" ? "hidden lg:block" : ""
      }`}
    >
      <Container>
        <div className="pt-12 pb-10 lg:pt-16 lg:pb-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
            <div className="lg:col-span-4 space-y-5 lg:space-y-6">
              <Link href="/" className="inline-block w-full max-w-[18rem] lg:max-w-[22rem]">
                {/* Local brand assets — CMS globals on staging/prod still point at legacy logos */}
                <Image
                  src="/brand/royal-show-logo-new.svg"
                  alt="Royal Adelaide Show"
                  width={755}
                  height={393}
                  className="object-contain w-full h-auto mix-blend-lighten"
                  unoptimized
                />
              </Link>

              {(data?.name || addressLine) && (
                <p className="font-body font-semibold text-brand-wool text-sm lg:text-base leading-snug text-pretty">
                  {data?.name && <span className="block">{data.name}</span>}
                  {addressLine && <span className="block">{addressLine}</span>}
                </p>
              )}

              {data?.contentRight && (
                <p className="font-body text-brand-wool/80 text-sm leading-relaxed text-pretty max-w-md">
                  {data.contentRight}
                </p>
              )}

              {data?.socials?.length > 0 && (
                <div className="flex items-center gap-3 pt-1">
                  {data.socials.map((social, index) => (
                    <Social
                      key={index}
                      type={social.social_type.value}
                      link={social.link}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-8">
              <FooterNav data={data} />
            </div>
          </div>

          <div className="mt-12 lg:mt-16 flex flex-col items-center sm:flex-row sm:items-end sm:justify-between gap-6">
            {data?.contentBottom && (
              <p className="font-body text-brand-wool/50 text-xs lg:text-sm leading-relaxed text-pretty max-w-3xl lg:max-w-4xl">
                {data.contentBottom}
              </p>
            )}

            <div className="flex flex-col items-center sm:items-end gap-3 shrink-0">
              <div className="w-[3.2rem] h-[3.2rem] sm:w-16 sm:h-16 lg:w-[4.8rem] lg:h-[4.8rem]">
                <Image
                  src="/brand/ras-rose.svg"
                  alt="Royal Adelaide Show rose"
                  width={204}
                  height={212}
                  className="object-contain w-full h-full mix-blend-lighten"
                  unoptimized
                />
              </div>
              <Link
                className="font-body text-brand-wool/50 text-xs lg:text-sm leading-relaxed whitespace-nowrap hover:text-brand-orange transition-colors"
                target="_blank"
                href="https://www.rockethouse.com.au/?utm_source=theshow&utm_medium=link&utm_campaign=portfolio&utm_content=footer"
              >
                Website by Rockethouse
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}

export function Social({ type, link }) {
  const className =
    "text-brand-fawn hover:text-brand-orange transition-colors inline-flex";

  switch (type) {
    case "facebook":
      return (
        <Link target="_blank" href={link} className={className} aria-label="Facebook">
          <FaSquareFacebook size={28} />
        </Link>
      );
    case "instagram":
      return (
        <Link target="_blank" href={link} className={className} aria-label="Instagram">
          <FaInstagram size={28} />
        </Link>
      );
    case "youtube":
      return (
        <Link target="_blank" href={link} className={className} aria-label="YouTube">
          <FaYoutube size={28} />
        </Link>
      );
    default:
      return null;
  }
}
